import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import {
  ClipboardPaste,
  Copy,
  Download,
  EllipsisVertical,
  Import,
} from 'lucide-react';
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap';

import Alert from 'src/componentes/message-box/alert';
import Confirm from 'src/componentes/message-box/confirm';
import ContentManager from 'src/componentes/content-manager';
import http from 'src/services/httpService';
import { DownloadFile, ParseBlobError } from 'src/services/downloadFileUrl';
import errorHandler from 'src/utils/error-handler';
import css from 'src/utils/css';
import FormularioGlobalImportacao from './FormularioGlobalImportacao';
import {
  copiarFormularioParaAreaTransferencia,
  isFormularioVazio,
  lerFormularioDaAreaTransferencia,
} from './formularioGlobalClipboard';

const classes = css`
  .toggle {
    align-items: center;
    background: transparent;
    border: 0;
    color: #73737f;
    display: inline-flex;
    padding: 4px;
  }
  .menu {
    border: 1px solid #e4e4e9;
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
    min-width: 260px;
    padding: 12px 0;
    z-index: 11001;
  }
  .item {
    align-items: center;
    color: #575763;
    display: flex;
    gap: 14px;
    padding: 12px 22px;
  }
`;

const FormularioGlobalCopiarColar = ({ disabled, formulario, onColar }) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [importacaoAberta, setImportacaoAberta] = useState(false);
  const [exportando, setExportando] = useState(false);
  const exportandoRef = useRef(false);
  const currentRef = useRef({ disabled, formulario, onColar });
  currentRef.current = { disabled, formulario, onColar };

  const showAlert = (id) =>
    ContentManager.addContent(<Alert message={intl.formatMessage({ id })} />);

  const handleExportar = async () => {
    if (exportandoRef.current) return;
    exportandoRef.current = true;
    setExportando(true);
    try {
      const response = await http.post(
        '/FormularioGlobal/Exportar',
        currentRef.current.formulario,
        { responseType: 'blob' }
      );
      DownloadFile(response);
    } catch (err) {
      errorHandler(await ParseBlobError(err));
    } finally {
      exportandoRef.current = false;
      setExportando(false);
    }
  };

  const handleCopiar = async () => {
    try {
      if (isFormularioVazio(formulario)) {
        showAlert('labelFormularioVazioParaCopiar');
        return;
      }

      const copiado = await copiarFormularioParaAreaTransferencia(formulario);

      if (copiado) {
        enqueueSnackbar(intl.formatMessage({ id: 'labelFormularioCopiado' }), {
          variant: 'success',
        });
      } else {
        showAlert('labelNaoFoiPossivelCopiarFormulario');
      }
    } catch (err) {
      errorHandler(err);
    }
  };

  const aplicarColagem = (
    formularioColado,
    messageId = 'labelFormularioColado'
  ) => {
    if (currentRef.current.disabled) return;
    currentRef.current.onColar?.(formularioColado);
    enqueueSnackbar(intl.formatMessage({ id: messageId }), {
      variant: 'success',
    });
  };

  const substituirFormulario = (novoFormulario, messageId) => {
    if (currentRef.current.disabled) return;
    if (isFormularioVazio(currentRef.current.formulario)) {
      aplicarColagem(novoFormulario, messageId);
      return;
    }
    ContentManager.addContent(
      <Confirm
        message={intl.formatMessage({
          id: 'labelSubstituirFormularioConfirmacao',
        })}
        handleConfirm={() => aplicarColagem(novoFormulario, messageId)}
      />
    );
  };

  const handleColar = async () => {
    if (currentRef.current.disabled) return;
    try {
      const formularioColado = await lerFormularioDaAreaTransferencia();

      if (!formularioColado) {
        showAlert('labelConteudoAreaTransferenciaNaoEhFormulario');
        return;
      }

      substituirFormulario(formularioColado);
    } catch (err) {
      errorHandler(err);
    }
  };

  return (
    <>
      <UncontrolledDropdown>
        <DropdownToggle
          className={classes.toggle}
          tag="button"
          type="button"
          aria-label={intl.formatMessage({ id: 'labelAcoesFormulario' })}
          title={intl.formatMessage({ id: 'labelAcoesFormulario' })}
        >
          <EllipsisVertical size={22} />
        </DropdownToggle>
        <DropdownMenu className={classes.menu} container="body" end>
          <DropdownItem className={classes.item} onClick={handleCopiar}>
            <Copy size={20} />
            {intl.formatMessage({ id: 'labelCopiarFormulario' })}
          </DropdownItem>
          {!disabled && (
            <DropdownItem className={classes.item} onClick={handleColar}>
              <ClipboardPaste size={20} />
              {intl.formatMessage({ id: 'labelColarFormulario' })}
            </DropdownItem>
          )}
          <DropdownItem
            className={classes.item}
            disabled={disabled}
            onClick={() => setImportacaoAberta(true)}
          >
            <Import size={20} />
            {intl.formatMessage({ id: 'labelImportarFormulario' })}
          </DropdownItem>
          <DropdownItem
            className={classes.item}
            disabled={exportando}
            onClick={handleExportar}
          >
            <Download size={20} />
            {intl.formatMessage({ id: 'labelExportarFormularioAtual' })}
          </DropdownItem>
        </DropdownMenu>
      </UncontrolledDropdown>
      {importacaoAberta && (
        <FormularioGlobalImportacao
          disabled={disabled}
          formulario={formulario}
          onClose={() => setImportacaoAberta(false)}
          onImportar={(importado) =>
            substituirFormulario(importado, 'msgFormularioImportado')
          }
        />
      )}
    </>
  );
};

export default FormularioGlobalCopiarColar;
